import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useTheme } from '@/contexts/ThemeContext';
import { Wrench, LayoutGrid, Hammer, CableCar, Cog, FileUp, BarChart3, Box, Sparkles } from 'lucide-react';

import PWMSummaryDashboard from '@/components/piping/PWMSummaryDashboard';
import PWMWorkfrontStatus from '@/components/piping/PWMWorkfrontStatus';
import PWMFabProgress from '@/components/piping/PWMFabProgress';
import PWMErectionProgress from '@/components/piping/PWMErectionProgress';
import PWMAllocationEngine from '@/components/piping/PWMAllocationEngine';
import PWMDataImport from '@/components/piping/PWMDataImport';
import PWMAnalytics from '@/components/piping/PWMAnalytics';
import WorkfrontModelTab from '@/components/workfront/WorkfrontModelTab';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import ModuleHelp from '@/components/common/ModuleHelp';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';
import api from '@/lib/api';

import { useProject } from '@/contexts/ProjectContext';

const TABS = [
  { id: 'summary', label: 'Summary', icon: BarChart3 },
  { id: 'insights', label: 'AI Insights', icon: Sparkles },
  { id: 'workfront', label: 'Workfront Status', icon: LayoutGrid },
  { id: 'fabrication', label: 'Fabrication Progress', icon: Hammer },
  { id: 'erection', label: 'Erection Progress', icon: CableCar },
  { id: '3dmodel', label: '3D Model', icon: Box },
  { id: 'allocation', label: 'Allocation Engine', icon: Cog },
  { id: 'import', label: 'Data & Registers', icon: FileUp },
  { id: 'analytics', label: 'KPIs & Analytics', icon: BarChart3 },
];

export default function PipingWorkfront() {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('summary');
  const { projectId, selectedProject } = useProject();
  const [searchParams] = useSearchParams();
  const [globalFilters, setGlobalFilters] = useState({ agUg: '', priority: '', area: '', status: '' });
  const [filterOptions, setFilterOptions] = useState({ areas: [], priorities: [] });

  // Reset state and fetch filter options when project changes
  useEffect(() => {
    setActiveTab('summary');
    // Pre-apply filters from URL search params (cross-module navigation)
    const urlArea = searchParams.get('area') || '';
    const urlStatus = searchParams.get('status') || '';
    const urlAgUg = searchParams.get('agUg') || '';
    const urlPriority = searchParams.get('priority') || '';
    setGlobalFilters({ agUg: urlAgUg, priority: urlPriority, area: urlArea, status: urlStatus });
    if (!projectId) return;
    (async () => {
      try {
        const res = await api.get(`/v1/projects/${projectId}/pwm/filter-options`);
        setFilterOptions(res.data || {});
      } catch { /* ignore */ }
    })();
  }, [projectId, searchParams]);

  return (
    <DashboardLayout>
      <div className="space-y-3 p-4 md:p-6" data-testid="piping-workfront-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: '#143A6B20', border: '1px solid #143A6B40' }}>
              <Wrench className="w-5 h-5" style={{ color: '#143A6B' }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Piping Workfront Allocation & Forecasting</h1>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                Workfront management, fabrication tracking (5 stage gates), erection progress (Inch-Meter)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ModuleHelp moduleKey="pwm" />
            <span className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
              data-testid="pwm-project-label">Project: {selectedProject?.name || projectId}</span>
          </div>
        </div>

        {/* AI Analyst Panel */}
        {projectId && (
          <AIAnalystPanel projectId={projectId} moduleKey="PIPING_WORKFRONT" defaultOpen={false} compact={true} />
        )}

        {/* Global Filter Bar */}
        <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }} data-testid="pwm-global-filters">
          <div className="flex gap-1">
            {['', 'AG', 'UG'].map(v => (
              <button key={v || 'all'} onClick={() => setGlobalFilters(f => ({ ...f, agUg: v }))} data-testid={`pwm-filter-agug-${v || 'all'}`}
                className="px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors"
                style={{ background: globalFilters.agUg === v ? '#143A6B' : 'var(--card-bg)', color: globalFilters.agUg === v ? '#fff' : 'var(--text-muted)', border: globalFilters.agUg === v ? 'none' : '1px solid var(--border-color)' }}>
                {v || 'All'}
              </button>
            ))}
          </div>
          <div className="w-px h-5" style={{ background: 'var(--border-color)' }} />
          <select value={globalFilters.priority} onChange={e => setGlobalFilters(f => ({ ...f, priority: e.target.value }))} data-testid="pwm-filter-priority"
            className="px-2 py-1 text-[10px] rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Priorities</option>
            {(filterOptions.priorities || [1, 2, 3, 4, 5]).map(p => <option key={p} value={p}>P{p}</option>)}
          </select>
          <select value={globalFilters.area} onChange={e => setGlobalFilters(f => ({ ...f, area: e.target.value }))} data-testid="pwm-filter-area"
            className="px-2 py-1 text-[10px] rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Areas</option>
            {(filterOptions.areas || []).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={globalFilters.status} onChange={e => setGlobalFilters(f => ({ ...f, status: e.target.value }))} data-testid="pwm-filter-status"
            className="px-2 py-1 text-[10px] rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Statuses</option>
            {['UNALLOCATED', 'PARTIAL_SOFT', 'READY_SOFT', 'PO_READY', 'PARTIAL_HARD', 'FULLY_ALLOCATED'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`pwm-tab-${tab.id}`}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg whitespace-nowrap transition-colors"
                style={{
                  color: activeTab === tab.id ? '#143A6B' : 'var(--text-muted)',
                  background: activeTab === tab.id ? 'var(--card-bg)' : 'transparent',
                  borderBottom: activeTab === tab.id ? '2px solid #143A6B' : '2px solid transparent',
                }}>
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content — each tab wrapped in its own ErrorBoundary so a
            render-time crash in one panel never blanks the whole page. */}
        {activeTab === 'summary'     && <ErrorBoundary name="pwm-summary"    title="Summary panel hit an error"><PWMSummaryDashboard projectId={projectId} filters={globalFilters} /></ErrorBoundary>}
        {activeTab === 'insights'    && <ErrorBoundary name="pwm-insights"   title="AI Insights panel hit an error"><ModuleInsightsDashboard projectId={projectId} moduleKey="pwm" color="#143A6B" deepLinkBase="/piping-workfront" /></ErrorBoundary>}
        {activeTab === 'workfront'   && <ErrorBoundary name="pwm-workfront"  title="Workfront Status panel hit an error"><PWMWorkfrontStatus projectId={projectId} filters={globalFilters} /></ErrorBoundary>}
        {activeTab === 'fabrication' && <ErrorBoundary name="pwm-fab"        title="Fabrication panel hit an error"><PWMFabProgress projectId={projectId} filters={globalFilters} /></ErrorBoundary>}
        {activeTab === 'erection'    && <ErrorBoundary name="pwm-erection"   title="Erection panel hit an error"><PWMErectionProgress projectId={projectId} filters={globalFilters} /></ErrorBoundary>}
        {activeTab === '3dmodel'     && <ErrorBoundary name="pwm-3d"         title="3D Model panel hit an error"><WorkfrontModelTab projectId={projectId} moduleKey="piping" /></ErrorBoundary>}
        {activeTab === 'allocation'  && <ErrorBoundary name="pwm-allocation" title="Allocation Engine panel hit an error"><PWMAllocationEngine projectId={projectId} /></ErrorBoundary>}
        {activeTab === 'import'      && <ErrorBoundary name="pwm-import"     title="Import panel hit an error"><PWMDataImport projectId={projectId} /></ErrorBoundary>}
        {activeTab === 'analytics'   && <ErrorBoundary name="pwm-analytics"  title="Analytics panel hit an error"><PWMAnalytics projectId={projectId} filters={globalFilters} /></ErrorBoundary>}
      </div>
    </DashboardLayout>
  );
}
